import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'react-bootstrap'
import { toast } from '@CommonComponents/toast'
import { useStateValue } from './state'
import { client } from '@Utils'
import { HighlightActionMenu } from './HighlightActionMenu'

export const Highlight = ({
  hId,
  note,
  sectionTitle,
  tags,
  anchor,
  cssClass,
  contentString,
}) => {
  let [currentState, dispatch] = useStateValue()
  let [hlColor, setHlColor] = useState(cssClass)
  let [hlTags, setHlTags] = useState(tags.map(inner => inner[1]).join(' '))
  let [hlNote, setHlNote] = useState(note)

  // Sync local display state when props change (e.g. after reader updates the highlight)
  useEffect(() => {
    if (!currentState.editingIDs.includes(hId)) {
      setHlColor(cssClass)
      setHlNote(note)
      setHlTags(tags.map(inner => inner[1]).join(' '))
    }
  }, [cssClass, note, tags])

  const saveHighlight = async () => {
    let data = {
      hId: hId,
      color: hlColor,
      note: hlNote,
      tags: hlTags,
    }
    try {
      let resp = await client.post(
        `/reader/notebook.update_highlight/${currentState.coursenum}/`,
        data
      )
      dispatch({
        type: 'updateHighlights',
        payload: currentState.highlights.map(hl =>
          hl.hId === resp.data.highlight.hId ? resp.data.highlight : hl
        ),
      })
      const updatedHl = resp.data.highlight
      const existingTagIds = new Set(currentState.tags.map(t => t[0]))
      const newTags = updatedHl.tags.filter(t => !existingTagIds.has(t[0]))
      if (newTags.length > 0) {
        dispatch({
          type: 'updateTags',
          payload: [...currentState.tags, ...newTags],
        })
      }
      window.dispatchEvent(
        new CustomEvent('notebookUpdatedHighlight', {
          detail: {
            highlightId: updatedHl.hId,
            updates: {
              note: updatedHl.note,
              tags: updatedHl.tags.map(([, name]) => name),
              css_class: updatedHl.cssClass,
            },
          },
        })
      )
      toast.success('Highlight changes saved')
    } catch (err) {
      toast.error(
        (err.response && err.response.data && err.response.data.message) ||
          String(err)
      )
    }
    dispatch({
      type: 'updateEditingIDs',
      payload: currentState.editingIDs.filter(id => id !== hId),
    })
  }

  const cancelHighlightEditing = hId => {
    // reset the form data
    setHlColor(cssClass)
    setHlTags(tags.map(inner => inner[1]).join(' '))
    setHlNote(note)

    dispatch({
      type: 'updateEditingIDs',
      payload: currentState.editingIDs.filter(id => id !== hId),
    })
  }

  const deleteHighlight = async hId => {
    try {
      await client.post(
        `/reader/notebook.delete_highlight/${currentState.coursenum}/`,
        { hId: hId }
      )
      dispatch({
        type: 'updateHighlights',
        payload: currentState.highlights.filter(hl => hl.hId !== hId),
      })
      dispatch({
        type: 'updateHighlightTotal',
        payload: currentState.highlightTotal - 1,
      })
      dispatch({
        type: 'updateToDeleteIds',
        payload: currentState.toDeleteIds.filter(item => item !== hId),
      })
      window.dispatchEvent(
        new CustomEvent('notebookDeletedHighlight', {
          detail: { highlightId: hId },
        })
      )
    } catch (err) {
      toast.error(
        (err.response && err.response.data && err.response.data.error) ||
          String(err)
      )
    }
  }

  const cancelDeleteHighlight = hId => {
    let newIds = currentState.toDeleteIds.filter(item => item !== hId)
    dispatch({
      type: 'updateToDeleteIds',
      payload: newIds,
    })
  }

  return (
    <React.Fragment>
      {currentState.toDeleteIds.includes(hId) ? (
        <div>
          <p>Are you sure you want to delete this highlight?</p>
          <div className="d-flex justify-content-around">
            <button
              className="btn btn-myed btn-danger btn-danger-myed"
              onClick={() => deleteHighlight(hId)}
            >
              Delete
            </button>
            <button
              className="btn btn-myed btn-default-myed"
              onClick={() => cancelDeleteHighlight(hId)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div id={`container-${hId}`}>
          <div
            className="highlight-content-container"
            style={{ borderBottom: '1px solid #EDEDED' }}
          >
            <HighlightActionMenu
              hId={hId}
              anchor={anchor}
              coursenum={currentState.coursenum}
            />
            <h2 className="w-75 mb-0 highlighter-title">
              <a
                className="text-decoration-none"
                href={`/reader/web/${currentState.coursenum}/${anchor}/`}
              >
                {sectionTitle}
              </a>
            </h2>
            {currentState.editingIDs.includes(hId) && (
              <div className="pt-2 px-3">
                <ColorPicker value={hlColor} onChange={setHlColor} />
              </div>
            )}
            <div className="pt-2 pr-2 pb-0 pl-3">
              <p className="my-2 ml-1 mr-2">
                {contentString ? (
                  <span
                    style={styles[hlColor]}
                    dangerouslySetInnerHTML={{ __html: contentString }}
                  />
                ) : (
                  <i>
                    <sup>*</sup>
                    The highlighted text can&apos;t be displayed right now.{' '}
                    <a
                      href="https://myeducator.freshdesk.com/support/home"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Learn why highlights may be unavailable.
                    </a>{' '}
                    You can navigate to the highlight through the Actions menu
                    above.
                  </i>
                )}
              </p>
            </div>
            {currentState.editingIDs.includes(hId) ? (
              <div className="px-3 mb-2">
                <textarea
                  className="form-control"
                  placeholder="Type in your notes here."
                  value={hlNote}
                  onChange={e => setHlNote(e.target.value)}
                  rows={3}
                />
              </div>
            ) : (
              hlNote && (
                <div id={hId} className="mb-2">
                  <p className="mb-0 pl-2" style={styles.noteText}>
                    {hlNote}
                  </p>
                </div>
              )
            )}
            {currentState.editingIDs.includes(hId) ? (
              <div className="px-3 mb-2">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Type in your tags here."
                  value={hlTags}
                  onChange={e => setHlTags(e.target.value)}
                />
                <div>
                  {hlTags
                    .trim()
                    .split(/\s+/)
                    .filter(t => t.length > 0)
                    .map((tempTag, i) => (
                      <TagPill tag={['', tempTag]} key={i} />
                    ))}
                </div>
              </div>
            ) : (
              <div className="ml-3 mb-2">
                {tags &&
                  tags.length > 0 &&
                  tags.map(tag => <TagPill tag={tag} key={tag[0]} />)}
              </div>
            )}
            {currentState.editingIDs.includes(hId) && (
              <div className="text-center mb-2">
                <Button
                  type="button"
                  bsStyle="primary"
                  className="btn btn-myed btn-primary btn-primary-myed mr-2"
                  onClick={saveHighlight}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  bsStyle="default"
                  className="btn btn-myed btn-default btn-default-myed"
                  onClick={() => cancelHighlightEditing(hId)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

Highlight.propTypes = {
  hId: PropTypes.string,
  cssClass: PropTypes.string,
  contentString: PropTypes.string,
  tags: PropTypes.array,
  anchor: PropTypes.string,
  note: PropTypes.string,
  sectionTitle: PropTypes.string,
}

function TagPill({ tag }) {
  const [currentState, dispatch] = useStateValue()

  const handleClick = val => {
    const currentValues = currentState.filters.tags
    const updatedValues = currentValues.includes(val)
      ? currentValues
      : [...currentValues, val]
    dispatch({
      type: 'updateFilters',
      payload: { ...currentState.filters, ['tags']: updatedValues },
    })
  }

  return (
    <button
      className="d-inline-flex border-0 align-items-center mr-1 cursor-pointer"
      onClick={() => handleClick(tag[0])}
      style={styles.tagPill}
    >
      {tag[1]}
    </button>
  )
}

export function ColorPicker({ value, onChange }) {
  const containerRef = useRef(null)

  const handleColorClick = cssClass => {
    if (onChange) {
      onChange(cssClass)
    }
  }

  const handleKeyDown = e => {
    // Select on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      return
    }

    // Arrow key navigation
    if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault()

      const currentIndex = allOptions.findIndex(opt => opt.cssClass === value)
      let nextIndex

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % allOptions.length
      } else {
        nextIndex = (currentIndex - 1 + allOptions.length) % allOptions.length
      }

      const nextOption = allOptions[nextIndex]
      handleColorClick(nextOption.cssClass)
    }
  }

  // Focus the selected item when value changes via keyboard
  useEffect(() => {
    if (containerRef.current && value) {
      const selectedElement = containerRef.current.querySelector(
        `[data-cssclass="${value}"]`
      )
      if (
        selectedElement &&
        document.activeElement &&
        document.activeElement.getAttribute('role') === 'radio'
      ) {
        selectedElement.focus()
      }
    }
  }, [value])

  const colorOptions = [
    { cssClass: 'user_highlight_yellow', label: 'Yellow highlight' },
    { cssClass: 'user_highlight_orange', label: 'Orange highlight' },
    { cssClass: 'user_highlight_green', label: 'Green highlight' },
    { cssClass: 'user_highlight_cyan', label: 'Cyan highlight' },
    { cssClass: 'user_highlight_purple', label: 'Purple highlight' },
    { cssClass: 'user_highlight_pink', label: 'Pink highlight' },
    { cssClass: 'user_highlight_blue', label: 'Blue highlight' },
    { cssClass: 'user_highlight_gray', label: 'Gray highlight' },
  ]

  const underlineOptions = [
    { cssClass: 'user_highlight_yellow_underline', label: 'Yellow underline' },
    { cssClass: 'user_highlight_orange_underline', label: 'Orange underline' },
    { cssClass: 'user_highlight_green_underline', label: 'Green underline' },
    { cssClass: 'user_highlight_cyan_underline', label: 'Cyan underline' },
    { cssClass: 'user_highlight_purple_underline', label: 'Purple underline' },
    { cssClass: 'user_highlight_pink_underline', label: 'Pink underline' },
    { cssClass: 'user_highlight_blue_underline', label: 'Blue underline' },
    { cssClass: 'user_highlight_gray_underline', label: 'Gray underline' },
  ]

  const allOptions = [...colorOptions, ...underlineOptions]

  return (
    <div
      className="color_picker_container"
      style={styles.container}
      role="radiogroup"
      aria-label="Highlight color selection"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <div className="color_picker_container_inner">
        {colorOptions.map(option => (
          <div
            key={option.cssClass}
            className="d-inline-block align-middle text-center color_picker_item cursor-pointer"
            data-cssclass={option.cssClass}
            style={{
              ...styles.colorPickerItem,
              ...styles[option.cssClass],
              ...(value === option.cssClass ? styles.selected : {}),
            }}
            onClick={() => handleColorClick(option.cssClass)}
            role="radio"
            aria-checked={value === option.cssClass}
            aria-label={option.label}
            tabIndex={value === option.cssClass ? 0 : -1}
          ></div>
        ))}
        <br />
        {underlineOptions.map(option => (
          <div
            key={option.cssClass}
            className="d-inline-block align-middle text-center color_picker_item cursor-pointer"
            data-cssclass={option.cssClass}
            style={{
              ...styles.colorPickerItem,
              border: '2px solid transparent',
              ...styles[option.cssClass],
              ...(value === option.cssClass ? styles.selected : {}),
            }}
            onClick={() => handleColorClick(option.cssClass)}
            role="radio"
            aria-checked={value === option.cssClass}
            aria-label={option.label}
            tabIndex={value === option.cssClass ? 0 : -1}
          >
            U
          </div>
        ))}
      </div>
    </div>
  )
}

const styles = {
  // Highlight
  noteText: {
    borderLeft: '3px solid var(--border-bold)',
    lineHeight: '1.5',
    fontFamily: 'sans-serif',
  },
  // TagPill
  tagPill: {
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#374151',
  },
  // ColorPicker
  container: {
    padding: '8px',
  },
  colorPickerItem: {
    width: '17px',
    height: '17px',
    fontSize: '12px',
    color: '#666',
    margin: '2px',
    border: '2px solid transparent',
  },
  selected: {
    border: '2px solid #000',
    outline: '2px solid #fff',
    outlineOffset: '-4px',
  },
  user_highlight_yellow: { backgroundColor: '#FFFF84' },
  user_highlight_orange: { backgroundColor: '#FFD98C' },
  user_highlight_green: { backgroundColor: '#A5FFDE' },
  user_highlight_cyan: { backgroundColor: '#A2FFFF' },
  user_highlight_purple: { backgroundColor: '#EBC7FF' },
  user_highlight_pink: { backgroundColor: '#FECEF0' },
  user_highlight_blue: { backgroundColor: '#A1DEFF' },
  user_highlight_gray: { backgroundColor: '#C9C9C9' },
  user_highlight_yellow_underline: { borderBottom: '2px solid #CCCC51' },
  user_highlight_orange_underline: { borderBottom: '2px solid #CCA659' },
  user_highlight_green_underline: { borderBottom: '2px solid #72CCAB' },
  user_highlight_cyan_underline: { borderBottom: '2px solid #6FCCCC' },
  user_highlight_purple_underline: { borderBottom: '2px solid #B894CC' },
  user_highlight_pink_underline: { borderBottom: '2px solid #CB9BBD' },
  user_highlight_blue_underline: { borderBottom: '2px solid #6EABCC' },
  user_highlight_gray_underline: { borderBottom: '2px solid #A0A0A0' },
}
